import { format, parseISO, differenceInMilliseconds } from "date-fns";
import { formatInTimeZone, toZonedTime, fromZonedTime } from "date-fns-tz";

// Target timezone for display and input interpretation
export const DISPLAY_TIMEZONE = "Europe/London";

/**
 * Convert a local date + time input (interpreted as Europe/London time) to a UTC ISO string for storage
 * This is the canonical function for converting user input → database storage
 * 
 * @param date - Date object (from date picker, represents the date portion)
 * @param time - Time string in "HH:mm" format (from time input)
 * @returns ISO 8601 UTC string ready for database storage
 */
export function localInputToUTC(date: Date, time: string): string {
  // Extract the date components from the Date object
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Parse the time string
  const [hours, minutes] = time.split(":").map(Number);
  
  // Create a date object representing the local time in Europe/London
  // We use fromZonedTime to properly handle DST
  const londonDate = new Date(year, month, day, hours, minutes, 0, 0);
  
  // Convert from London timezone to UTC
  const utcDate = fromZonedTime(londonDate, DISPLAY_TIMEZONE);
  
  return utcDate.toISOString();
}

/**
 * Check if a deadline has passed (comparing against current time)
 * Uses proper UTC comparison
 * 
 * @param deadlineUtc - UTC ISO string of the deadline
 * @returns true if deadline has passed
 */
export function isDeadlinePassed(deadlineUtc: string): boolean {
  try {
    const deadline = parseISO(deadlineUtc);
    const now = new Date();
    return now > deadline;
  } catch (error) {
    console.error("Error checking deadline:", error);
    return false;
  }
}

/**
 * Format a UTC date string for display in Europe/London timezone
 * Handles DST automatically
 */
export function formatInLondon(utcDateString: string, formatStr: string = "dd MMM yyyy, HH:mm"): string {
  try {
    const date = parseISO(utcDateString);
    return formatInTimeZone(date, DISPLAY_TIMEZONE, formatStr);
  } catch (error) {
    console.error("Error formatting date:", error);
    return utcDateString;
  }
}

/**
 * Format a UTC date string with explicit timezone label for clarity
 * Example output: "19 Dec 2025, 17:00 (London)"
 */
export function formatWithTimezoneLabel(utcDateString: string): string {
  try {
    const date = parseISO(utcDateString);
    const formatted = formatInTimeZone(date, DISPLAY_TIMEZONE, "dd MMM yyyy, HH:mm");
    return `${formatted} (London)`;
  } catch (error) {
    console.error("Error formatting date with timezone:", error);
    return utcDateString;
  }
}

/**
 * Get a zoned Date object in Europe/London timezone
 */
export function toLocalZone(utcDateString: string): Date {
  const date = parseISO(utcDateString);
  return toZonedTime(date, DISPLAY_TIMEZONE);
}

/**
 * Calculate countdown values from now to target UTC timestamp
 * Uses server time if available, otherwise client time
 */
export function getCountdown(targetUtc: string, serverTimeNow?: Date): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
  isExpired: boolean;
} {
  try {
    const target = parseISO(targetUtc);
    const now = serverTimeNow || new Date();
    const totalMs = differenceInMilliseconds(target, now);
    
    if (totalMs <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, isExpired: true };
    }
    
    const totalSeconds = Math.floor(totalMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return { days, hours, minutes, seconds, totalMs, isExpired: false };
  } catch (error) {
    console.error("Error calculating countdown:", error);
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, isExpired: true };
  }
}

/**
 * Format a countdown for display
 */
export function formatCountdown(countdown: ReturnType<typeof getCountdown>): string {
  if (countdown.isExpired) return "Ended";
  
  if (countdown.days > 0) {
    return `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`;
  }
  if (countdown.hours > 0) {
    return `${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`;
  }
  if (countdown.minutes > 0) {
    return `${countdown.minutes}m ${countdown.seconds}s`;
  }
  return `${countdown.seconds}s`;
}

/**
 * Format date with full detail including timezone indicator
 */
export function formatFullDateTime(utcDateString: string): string {
  return formatInLondon(utcDateString, "dd MMM yyyy, HH:mm (zzz)");
}

/**
 * Get short date format
 */
export function formatShortDate(utcDateString: string): string {
  return formatInLondon(utcDateString, "dd MMM yyyy");
}

/**
 * Get time only format
 */
export function formatTimeOnly(utcDateString: string): string {
  return formatInLondon(utcDateString, "HH:mm");
}

/**
 * Calculate auction end time from start date and duration
 * 
 * @param auctionStartUtc - UTC ISO string for auction start
 * @param durationValue - Duration value (e.g., "30min", "1hour", "2hours", "1day", "1week", "custom")
 * @param customWeeks - Custom weeks (when durationValue is "custom")
 * @param customDays - Custom days (when durationValue is "custom")
 * @param customHours - Custom hours (when durationValue is "custom")
 * @returns UTC ISO string for auction end
 */
export function calculateAuctionEnd(
  auctionStartUtc: string,
  durationValue: string,
  customWeeks?: number,
  customDays?: number,
  customHours?: number
): string {
  const startDate = parseISO(auctionStartUtc);
  
  let durationMs: number;
  
  if (durationValue === "custom") {
    const weeks = customWeeks || 0;
    const days = customDays || 0;
    const hours = customHours || 0;
    durationMs = (weeks * 7 * 24 + days * 24 + hours) * 60 * 60 * 1000;
  } else {
    // Parse duration value based on the format from auctionDurationOptions
    // Values: "30min", "1hour", "2hours", "4hours", "1day", "2days", "1week"
    let hours = 24; // Default to 24 hours
    
    switch (durationValue) {
      case "30min":
        hours = 0.5;
        break;
      case "1hour":
        hours = 1;
        break;
      case "2hours":
        hours = 2;
        break;
      case "4hours":
        hours = 4;
        break;
      case "1day":
        hours = 24;
        break;
      case "2days":
        hours = 48;
        break;
      case "1week":
        hours = 168; // 7 * 24
        break;
      default:
        // Try to parse as a number for backwards compatibility
        const parsed = parseInt(durationValue, 10);
        if (!isNaN(parsed)) {
          hours = parsed;
        }
        break;
    }
    
    durationMs = hours * 60 * 60 * 1000;
  }
  
  const endDate = new Date(startDate.getTime() + durationMs);
  return endDate.toISOString();
}
